import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";

import type { TipTapEditor } from "../types";

/**
 * Core editor context type
 * Base context for all TipTap editors
 */
export type EditorContextType = {
  editor: TipTapEditor | null;
  setEditor: (editor: TipTapEditor | null) => void;
};

/**
 * Creates a typed editor context with proper error handling
 * Used by child editors to create their specific contexts
 */
export function createEditorContext<
  T extends EditorContextType = EditorContextType,
>() {
  return createContext<T | null>(null);
}

/**
 * Core EditorProvider component
 * Provides basic editor state management
 * Child editors can extend this or use their own provider
 */
const EditorContext = createEditorContext<EditorContextType>();

export function EditorProvider({ children }: PropsWithChildren) {
  const [editor, setEditor] = useState<TipTapEditor | null>(null);

  const value = useMemo(
    () => ({
      editor,
      setEditor,
    }),
    [editor],
  );

  return (
    <EditorContext.Provider value={value}>{children}</EditorContext.Provider>
  );
}

/**
 * Core useEditor hook
 * Returns editor instance with proper error handling
 */
export function useEditor(componentName?: string): EditorContextType {
  const context = useContext(EditorContext);
  if (!context) {
    const name = componentName || "Component";
    throw new Error(`${name} must be used within an EditorProvider`);
  }
  return context;
}

export { EditorContext };
